import { useEffect, useRef, useCallback } from 'react';

/**
 * useIdleTimer
 * Detects user inactivity and calls onIdle after `timeoutMs` milliseconds.
 * Shows a warning dialog `warningMs` before the timeout.
 *
 * @param {object} options
 * @param {number}   options.timeoutMs   - Idle timeout in ms (default: 15 minutes)
 * @param {number}   options.warningMs   - Warning shown before logout (default: 2 minutes)
 * @param {function} options.onIdle      - Called when session expires
 * @param {function} options.onWarning   - Called when warning countdown starts
 * @param {function} options.onActive    - Called when user becomes active again
 * @param {boolean}  options.enabled     - Enable/disable the timer (default: true)
 */
const useIdleTimer = ({
  timeoutMs  = 15 * 60 * 1000,   // 15 minutes
  warningMs  = 2  * 60 * 1000,   // 2 minutes warning
  onIdle     = () => {},
  onWarning  = () => {},
  onActive   = () => {},
  enabled    = true,
} = {}) => {
  const idleTimer    = useRef(null);
  const warnTimer    = useRef(null);
  const isWarning    = useRef(false);
  const lastActivity = useRef(Date.now());

  const clearTimers = () => {
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);
  };

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    clearTimers();

    // If we were in warning state, notify active again
    if (isWarning.current) {
      isWarning.current = false;
      onActive();
    }

    lastActivity.current = Date.now();

    // Set warning timer (fires before logout)
    warnTimer.current = setTimeout(() => {
      isWarning.current = true;
      onWarning();
    }, timeoutMs - warningMs);

    // Set logout timer
    idleTimer.current = setTimeout(() => {
      isWarning.current = false;
      onIdle();
    }, timeoutMs);
  }, [enabled, timeoutMs, warningMs, onIdle, onWarning, onActive]);

  useEffect(() => {
    if (!enabled) return;

    // Events that count as user activity
    const events = [
      'mousemove', 'mousedown', 'keydown',
      'touchstart', 'touchmove', 'wheel', 'scroll', 'click'
    ];

    const handleActivity = () => resetTimer();

    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    // Start the timer immediately
    resetTimer();

    return () => {
      clearTimers();
      events.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, [enabled, resetTimer]);

  return {
    /** Manually reset the idle timer (e.g. after an API call) */
    reset: resetTimer,
    /** ms since last activity */
    getLastActivity: () => Date.now() - lastActivity.current,
  };
};

export default useIdleTimer;